"use client";

import { Check, Code2, Copy, Loader2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslations } from "next-intl";
import { toast } from "sonner";
import { ApiError } from "@/lib/api";
import { SITE_URL } from "@/lib/site-url";
import { useIdePlan, useUpdateSnippet } from "../hooks";
import type { SnippetEmbedSettings } from "../types";

/** Mirrors the backend's cap; the server is what enforces it. */
const MAX_ORIGINS = 20;
const DEFAULT_WIDTH_PERCENT = 100;
const DEFAULT_HEIGHT_PX = 480;

export type EmbedSnippetPanelProps = {
  locale: string;
  snippetId: string;
  shareSlug: string | null;
  /** A protected share is never embeddable; the panel says so instead of lying. */
  isPasswordProtected: boolean;
  initialEmbed?: SnippetEmbedSettings;
};

function buildEmbedUrl(locale: string, shareSlug: string): string {
  return `${SITE_URL.replace(/\/$/, "")}/${locale}/playground/embed/${encodeURIComponent(shareSlug)}`;
}

/**
 * The iframe tag the owner copies (`PG-EMBED01`).
 *
 * `sandbox` is the restrictive posture the task asks for: scripts and the
 * frame's own origin are needed (Monaco, and a same-origin document so the
 * run request carries a real `Origin` header), and popups are what let the
 * wordmark open Progsity outside the publisher's page. Everything else the
 * attribute gates, top-level navigation included, stays off.
 */
export function buildEmbedCode(url: string, widthPercent: number, heightPx: number): string {
  const width = Number.isFinite(widthPercent)
    ? Math.min(100, Math.max(20, Math.round(widthPercent)))
    : DEFAULT_WIDTH_PERCENT;
  const height = Number.isFinite(heightPx)
    ? Math.min(2000, Math.max(240, Math.round(heightPx)))
    : DEFAULT_HEIGHT_PX;
  return [
    `<iframe src="${url}"`,
    ` width="${width}%" height="${height}"`,
    ' title="Progsity IDE"',
    ' loading="lazy" referrerpolicy="strict-origin-when-cross-origin"',
    ' sandbox="allow-scripts allow-same-origin allow-popups"',
    ' style="border: 1px solid rgba(255,255,255,0.1); border-radius: 10px;"></iframe>',
  ].join("");
}

/** Splits the owner's textarea into candidate origins, one per line or comma. */
export function parseOriginInput(raw: string): string[] {
  return raw
    .split(/[\n,]/)
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
}

/**
 * The owner's embed controls, inside the share dropdown (`PG-EMBED01`).
 *
 * The plan check here is a display affordance only: the backend refuses a
 * `enabled: true` write from a free account, and refuses an origin that is not
 * an origin, whatever this panel shows.
 */
export function EmbedSnippetPanel({
  locale,
  snippetId,
  shareSlug,
  isPasswordProtected,
  initialEmbed,
}: EmbedSnippetPanelProps) {
  const t = useTranslations("playground");
  const planQuery = useIdePlan();
  const updateMutation = useUpdateSnippet();

  const [enabled, setEnabled] = useState(initialEmbed?.enabled ?? false);
  const [originsText, setOriginsText] = useState(
    (initialEmbed?.allowedOrigins ?? []).join("\n"),
  );
  const [width, setWidth] = useState(DEFAULT_WIDTH_PERCENT);
  const [height, setHeight] = useState(DEFAULT_HEIGHT_PX);
  const [copied, setCopied] = useState(false);

  const plan = planQuery.data?.plan;
  const canEnable = plan === "pro" || plan === "internal";
  const embedUrl = shareSlug ? buildEmbedUrl(locale, shareSlug) : "";
  const code = useMemo(
    () => (embedUrl ? buildEmbedCode(embedUrl, width, height) : ""),
    [embedUrl, width, height],
  );

  async function save(nextEnabled: boolean): Promise<void> {
    const origins = parseOriginInput(originsText);
    if (origins.length > MAX_ORIGINS) {
      toast.error(t("embedOriginsTooMany", { max: MAX_ORIGINS }));
      return;
    }
    try {
      const snippet = await updateMutation.mutateAsync({
        id: snippetId,
        // Revoking never restates the list, so a free owner whose plan lapsed
        // can always turn a live embed off (ADR-0050).
        input: { embed: nextEnabled ? { enabled: true, allowedOrigins: origins } : { enabled: false } },
      });
      setEnabled(snippet.embed?.enabled ?? nextEnabled);
      if (snippet.embed?.allowedOrigins) {
        setOriginsText(snippet.embed.allowedOrigins.join("\n"));
      }
      toast.success(nextEnabled ? t("embedEnabledToast") : t("embedDisabledToast"));
    } catch (error) {
      const message = error instanceof ApiError ? error.message : t("embedSaveFailed");
      toast.error(message);
    }
  }

  async function copyCode(): Promise<void> {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success(t("toastCopied"));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t("toastCopyFailed"));
    }
  }

  return (
    <div className="mt-4 border-t border-white/10 pt-4">
      <div className="flex items-center gap-2">
        <Code2 className="h-4 w-4 text-[#E94D1D]" aria-hidden />
        <p className="text-sm font-medium text-white">{t("embedTitle")}</p>
        {enabled ? (
          <span className="ml-auto rounded-full border border-emerald-400/30 px-2 py-0.5 text-[11px] text-emerald-300">
            {t("embedOn")}
          </span>
        ) : (
          <span className="ml-auto rounded-full border border-white/10 px-2 py-0.5 text-[11px] text-white/55">
            {t("embedOff")}
          </span>
        )}
      </div>
      <p className="mt-1 text-xs leading-5 text-white/50">{t("embedHelp")}</p>

      {isPasswordProtected ? (
        <p className="mt-3 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs leading-5 text-white/60">
          {t("embedProtectedBlocked")}
        </p>
      ) : !shareSlug ? (
        <p className="mt-3 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs leading-5 text-white/60">
          {t("embedNeedsShare")}
        </p>
      ) : (
        <>
          <label className="mt-3 block text-xs font-medium text-white/70" htmlFor="embed-origins">
            {t("embedOriginsLabel")}
          </label>
          <textarea
            id="embed-origins"
            rows={3}
            value={originsText}
            onChange={(event) => setOriginsText(event.target.value)}
            placeholder={t("embedOriginsPlaceholder")}
            disabled={!canEnable}
            className="mt-1.5 w-full rounded-lg border border-white/15 bg-[#0B0B0C] px-3 py-2 font-mono text-[11px] text-white outline-none transition placeholder:text-white/30 focus:border-[#E94D1D]/70 focus:ring-2 focus:ring-[#E94D1D]/20 disabled:opacity-50"
          />
          <p className="mt-1 text-[11px] leading-4 text-white/45">{t("embedOriginsHelp")}</p>

          <div className="mt-3 flex gap-2">
            {canEnable ? (
              <button
                type="button"
                disabled={updateMutation.isPending}
                onClick={() => void save(!enabled)}
                className="inline-flex h-11 flex-1 items-center justify-center gap-2 rounded-lg bg-[#E94D1D] px-3 text-sm font-semibold text-white transition hover:bg-[#d94318] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {updateMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                ) : null}
                {enabled ? t("embedSaveOrDisable") : t("embedEnable")}
              </button>
            ) : enabled ? (
              // A downgrade keeps a live embed serving and freezes its settings,
              // but revoking is never gated (ADR-0049's rule, applied here).
              <button
                type="button"
                disabled={updateMutation.isPending}
                onClick={() => void save(false)}
                className="inline-flex h-11 flex-1 items-center justify-center rounded-lg border border-white/15 px-3 text-sm font-medium text-white/80 transition hover:bg-white/10 disabled:opacity-50"
              >
                {t("embedDisable")}
              </button>
            ) : (
              <p className="flex-1 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs leading-5 text-white/60">
                {t("embedProRequired")}
              </p>
            )}
            {canEnable && enabled ? (
              <button
                type="button"
                disabled={updateMutation.isPending}
                onClick={() => void save(false)}
                className="inline-flex h-11 items-center justify-center rounded-lg border border-white/15 px-3 text-sm font-medium text-white/80 transition hover:bg-white/10 disabled:opacity-50"
              >
                {t("embedDisable")}
              </button>
            ) : null}
          </div>

          {enabled ? (
            <div className="mt-4">
              <div className="flex gap-2">
                <label className="flex-1 text-[11px] font-medium text-white/60">
                  {t("embedWidthLabel")}
                  <input
                    type="number"
                    min={20}
                    max={100}
                    value={width}
                    onChange={(event) => setWidth(Number(event.target.value))}
                    className="mt-1 h-9 w-full rounded-lg border border-white/15 bg-[#0B0B0C] px-2 text-sm text-white outline-none focus:border-[#E94D1D]/70"
                  />
                </label>
                <label className="flex-1 text-[11px] font-medium text-white/60">
                  {t("embedHeightLabel")}
                  <input
                    type="number"
                    min={240}
                    max={2000}
                    value={height}
                    onChange={(event) => setHeight(Number(event.target.value))}
                    className="mt-1 h-9 w-full rounded-lg border border-white/15 bg-[#0B0B0C] px-2 text-sm text-white outline-none focus:border-[#E94D1D]/70"
                  />
                </label>
              </div>
              <pre className="mt-2 max-h-32 overflow-auto whitespace-pre-wrap break-all rounded-lg border border-white/10 bg-[#0B0B0C] px-2 py-2 font-mono text-[10px] leading-4 text-white/80">
                {code}
              </pre>
              <button
                type="button"
                onClick={() => void copyCode()}
                className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm font-medium text-white transition hover:bg-white/10"
              >
                {copied ? (
                  <Check className="h-4 w-4 text-emerald-400" aria-hidden />
                ) : (
                  <Copy className="h-4 w-4 text-[#E94D1D]" aria-hidden />
                )}
                {t("embedCopyCode")}
              </button>
              <p className="mt-2 text-center text-[11px] text-white/40">{t("embedPoweredBy")}</p>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
